<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        :title="pageTitle"
      >
        <template v-slot:preview>
          <b-form @submit.prevent="onSubmit">
            <!-- ====== HEADER ====== -->
            <b-row class="mb-3">
              <b-col cols="3">
                <b-form-group>
                  <template #label>Doanh nghiệp <span class="text-danger">*</span></template>
                  <b-form-select
                    size="sm"
                    v-model="form.companyId"
                    :options="listCompany"
                    value-field="id"
                    text-field="name"
                    required
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled>-- Doanh nghiệp --</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <template #label>Phòng ban <span class="text-danger">*</span></template>
                  <Autosuggest
                    :model="departmentModel"
                    :suggestions="departmentSuggestions"
                    :suggestionName="'name'"
                    :placeholder="'phòng ban'"
                    :limit="50"
                    :disabled="isLoadingFilters"
                    @select="onSelectedDepartment"
                    @change="onInputDepartment"
                  >
                    <template #custom="{ suggestion }">{{ suggestion.item.name }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <b-form-group>
                  <template #label>Tháng <span class="text-danger">*</span></template>
                  <b-form-select v-model.number="form.month" :options="monthOptions" size="sm" />
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <b-form-group>
                  <template #label>Năm <span class="text-danger">*</span></template>
                  <date-picker
                    ref="yearDatePicker"
                    class="form-control-sm"
                    :config="yearPickerConfig"
                    v-model="yearModel"
                    placeholder="Chọn năm"
                    @input="onYearChange"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="8">
                <b-form-group label="Diễn giải">
                  <b-form-textarea v-model="form.description" rows="2" placeholder="Nhập diễn giải chung cho phiếu" />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- ====== LINES GRID ====== -->
            <b-row class="mb-2">
              <b-col>
                <div class="d-flex align-items-center justify-content-between">
                  <h6 class="mb-0">
                    Chi tiết khoản mục phí
                    <b-badge variant="info" class="ml-1">{{ form.lines.length }} dòng</b-badge>
                    <b-badge v-if="totalAmount > 0" variant="success" class="ml-1">
                      Tổng: {{ convertPrice(totalAmount) }} đ
                    </b-badge>
                  </h6>
                  <div>
                    <b-button size="sm" variant="outline-primary" @click="openImportModal">
                      <i class="fas fa-file-excel"></i> Import Excel
                    </b-button>
                  </div>
                </div>
              </b-col>
            </b-row>

            <PurchaseRequestDetails
              :lines="form.lines"
              :total-amount="totalAmount"
              :tbody-tr-class="getLineRowClass"
              class="mb-1"
              @delete-line="removeLine(form.lines.indexOf($event))"
            >
              <template #cell-store="{ item, realIndex }">
                <Autosuggest
                  :model="item._storeModel"
                  :suggestions="item._storeSuggestions || storeSource.slice(0, 20)"
                  :suggestionName="'name'"
                  :placeholder="'cửa hàng'"
                  :limit="20"
                  @select="onLineSelectStore($event, realIndex)"
                  @change="onLineInputStore($event, realIndex)"
                >
                  <template #custom="{ suggestion }">{{ suggestion.item.name }}</template>
                </Autosuggest>
              </template>
              <template #cell-costItem="{ item, realIndex }">
                <Autosuggest
                  :model="item._costItemModel"
                  :suggestions="item._costItemSuggestions || (item.storeId ? [] : costItemSource.slice(0, 20))"
                  :suggestionName="'name'"
                  :placeholder="item.storeId ? 'khoản mục phí (theo budget)' : 'khoản mục phí'"
                  :limit="20"
                  :disabled="!!item._budgetWarning"
                  @select="onLineSelectCostItem($event, realIndex)"
                  @change="onLineInputCostItem($event, realIndex)"
                >
                  <template #custom="{ suggestion }">{{ suggestion.item.name }}</template>
                </Autosuggest>
                <small v-if="item._budgetWarning" class="text-warning d-block mt-1">
                  <i class="fas fa-exclamation-triangle"></i> {{ item._budgetWarning }}
                </small>
              </template>
              <template #cell-budgetAllocatedAmount="{ item }">
                <span v-if="item._budgetAllocatedAmount != null" class="text-info">
                  {{ convertPrice(item._budgetAllocatedAmount) }}
                </span>
                <span v-else-if="item._budgetLoading" class="text-muted">
                  <i class="fas fa-spinner fa-spin"></i>
                </span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell-requestedAmount="{ item, realIndex }">
                <BInputNumber
                  v-model.number="item.requestedAmount"
                  placeholder="0"
                  size="sm"
                  @valueChange="onLineAmountChange(realIndex)"
                />
              </template>
              <template #cell-budgetUsagePeriod="{ item, realIndex }">
                <b-form-input
                  v-model.number="item.budgetUsagePeriod"
                  type="number"
                  min="1"
                  max="12"
                  size="sm"
                  @input="onLineAmountChange(realIndex)"
                />
              </template>
              <template #cell-budgetRemainingAfterPR="{ item }">
                <span
                  v-if="item._budgetRemainingAfterPR != null"
                  :class="item._budgetRemainingAfterPR < 0 ? 'text-danger' : 'text-success'"
                >{{ convertPrice(item._budgetRemainingAfterPR) }}</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell-actions="{ realIndex }">
                <span
                  class="flaticon2-rubbish-bin-delete-button text-danger"
                  style="cursor:pointer; font-size:1rem"
                  @click="removeLine(realIndex)"
                />
              </template>
            </PurchaseRequestDetails>

            <div class="mb-3">
              <b-button size="sm" variant="primary" @click="addLine">
                <i class="fas fa-plus"></i> Thêm dòng
              </b-button>
            </div>

            <!-- Duplicate warning -->
            <b-alert v-if="duplicateWarnings.length > 0" show variant="warning" class="mb-3">
              <i class="fas fa-exclamation-triangle"></i>
              <span v-for="w in duplicateWarnings" :key="w">{{ w }}<br /></span>
            </b-alert>

            <!-- File attachments -->
            <b-row class="mb-4">
              <b-col cols="8">
                <b-form-group label="Tệp đính kèm">
                  <FileUploadManager
                    v-if="form.id"
                    :entity-id="form.id"
                    entity="purchase-requests"
                    :disabled="isSaving"
                  />
                  <small v-else class="text-muted">Lưu phiếu trước để đính kèm tệp.</small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-4">
              <b-col cols="8">
                <ApprovalStepSearchSelector
                  :steps="approvalSteps"
                  emptyText="Chưa có cấu hình duyệt cho loại phiếu này."
                  @change="approvalSteps = $event"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <b-button type="submit" variant="primary" :disabled="isSaving">
                  <i class="fas fa-save"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
                </b-button>
                <b-button variant="secondary" class="ml-2" @click="goBack">
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </KTCodePreview>
    </div>

    <!-- Excel Import Modal -->
    <ExcelImportModal
      v-if="showImportModal"
      :cost-item-source="costItemSource"
      :store-source="storeSource"
      @import="onExcelImported"
      @close="showImportModal = false"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { MODULE_ABBR, MODULE_LABELS_LOWER } from '@/core/config/menu/moduleLabels';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice, xoa_dau, handleError } from '@/utils/common';
import { debounce } from 'lodash';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
import ExcelImportModal from './components/ExcelImportModal.vue';
import PurchaseRequestDetails from './components/PurchaseRequestDetails.vue';
import timeUtils from '@/utils/date';
import moment from 'moment';

let _lineKeyCounter = 1;
const newLineKey = () => `line-${_lineKeyCounter++}`;

export default {
  components: { KTCodePreview, Autosuggest, BInputNumber, ApprovalStepSearchSelector, FileUploadManager, ExcelImportModal, PurchaseRequestDetails },
  data() {
    const currentDate = new Date();
    return {
      isEdit: false,
      isSaving: false,
      approvalSteps: [],
      approvalConfigDetails: [],
      approvalType: 9,
      listCompany: [],
      isLoadingFilters: false,
      suggestLimit: 50,
      departmentModel: '',
      yearModel: moment(String(currentDate.getFullYear()), 'YYYY'),
      yearPickerConfig: {
        ...timeUtils.DP_CONFIG.date,
        format: 'YYYY',
        viewMode: 'years',
        ignoreReadonly: true,
        allowInputToggle: true,
        useCurrent: false,
        showClear: true,
        showClose: true,
      },
      departmentSource: [],
      departmentSuggestions: [],
      storeSource: [],
      costItemSource: [],
      showImportModal: false,
      form: {
        id: null,
        companyId: null,
        departmentId: null,
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        description: '',
        lines: [],
      },
      monthOptions: Array.from({ length: 12 }, (_, i) => ({ value: i + 1, text: `Tháng ${i + 1}` })),
    };
  },
  watch: {
    'form.departmentId'() { this.recheckAllLineBudgets(); },
    'form.companyId'() { this.recheckAllLineBudgets(); },
    'form.month'() { this.recheckAllLineBudgets(); },
    'form.year'() { this.recheckAllLineBudgets(); },
  },
  computed: {
    pageTitle() {
      const label = MODULE_LABELS_LOWER.PURCHASE_REQUEST;
      return this.isEdit ? `Sửa ${label}` : `Tạo ${label}`;
    },
    totalAmount() {
      return this.form.lines.reduce((s, l) => s + (Number(l.requestedAmount) || 0), 0);
    },
    duplicateWarnings() {
      const seen = {};
      const warnings = [];
      this.form.lines.forEach((line, idx) => {
        if (!line.costItemId || !line.storeId) return;
        const key = `${line.costItemId}-${line.storeId}`;
        if (seen[key] != null) {
          warnings.push(`Dòng ${line.lineNumber}: trùng (khoản mục + cửa hàng) với dòng ${this.form.lines[seen[key]].lineNumber}`);
        } else {
          seen[key] = idx;
        }
      });
      return warnings;
    },
  },
  created() {
    this.fetchCompany();
    this.fetchCostItems();
    this.fetchDepartments();
    this.fetchStores();
    this.fetchApprovalConfig();
    const id = this.$route.query.id;
    if (id) {
      this.isEdit = true;
      this.fetchDetail(id);
    }
    this.debouncedCheckBudgetLine = debounce(this.checkBudgetForLine, 500);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Phiếu đề nghị mua hàng', route: '/budget/purchase-requests' },
      { title: this.isEdit ? `Sửa phiếu (${MODULE_ABBR.PURCHASE_REQUEST})` : `Tạo phiếu (${MODULE_ABBR.PURCHASE_REQUEST})` },
    ]);
    this.$nextTick(() => {
      const root = this.$refs.yearDatePicker && this.$refs.yearDatePicker.$el;
      const input = root && (root.tagName === 'INPUT' ? root : root.querySelector && root.querySelector('input'));
      if (input) {
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('autocomplete', 'off');
      }
    });
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,

    recheckAllLineBudgets() {
      this.form.lines.forEach((line, idx) => {
        if (line.costItemId) this.checkBudgetForLine(idx);
        if (line.storeId) this.loadBudgetCostItemsForLine(idx);
      });
    },

    getLineRowClass(item, type) {
      if (type !== 'row') return '';
      const idx = this.form.lines.indexOf(item);
      return idx !== -1 && this.lineHasDuplicate(idx) ? 'table-warning' : '';
    },
    lineHasDuplicate(idx) {
      const line = this.form.lines[idx];
      if (!line.costItemId || !line.storeId) return false;
      const key = `${line.costItemId}-${line.storeId}`;
      return this.form.lines.some((l, i) => i !== idx && l.costItemId === line.costItemId && l.storeId === line.storeId && key === `${l.costItemId}-${l.storeId}`);
    },

    reindexLines() {
      this.form.lines.forEach((line, i) => { line.lineNumber = i + 1; });
    },

    addLine() {
      this.form.lines.push({
        _key: newLineKey(),
        lineNumber: this.form.lines.length + 1,
        costItemId: null,
        storeId: null,
        requestedAmount: 0,
        budgetUsagePeriod: 1,
        description: null,
        _costItemModel: '',
        _storeModel: '',
        _costItemSuggestions: this.costItemSource.slice(0, 20),
        _storeSuggestions: this.storeSource.slice(0, 20),
        _budgetAllocatedAmount: null,
        _budgetRemainingAfterPR: null,
        _budgetLoading: false,
        _budgetWarning: null,
      });
    },

    removeLine(idx) {
      this.form.lines.splice(idx, 1);
      this.reindexLines();
    },

    filterByName(source, text, limit = 20) {
      const t = (text || '').toString().toLowerCase();
      if (!t.trim()) return source.slice(0, limit);
      return source
        .filter((item) => {
          const name = item.name || '';
          return xoa_dau(name).toLowerCase().includes(t) || name.toLowerCase().includes(t);
        })
        .slice(0, limit);
    },

    onLineSelectCostItem(option, idx) {
      const item = option && option.item ? option.item : null;
      this.$set(this.form.lines[idx], 'costItemId', item ? Number(item.id) : null);
      this.$set(this.form.lines[idx], '_costItemModel', item ? item.name : '');
      this.triggerLineBudgetCheck(idx);
    },
    onLineInputCostItem(text, idx) {
      this.$set(this.form.lines[idx], '_costItemModel', text || '');
      if (!text) this.$set(this.form.lines[idx], 'costItemId', null);
      this.$set(this.form.lines[idx], '_costItemSuggestions', this.filterByName(this.costItemSource, text));
    },
    onLineSelectStore(option, idx) {
      const item = option && option.item ? option.item : null;
      const prevStoreId = this.form.lines[idx]?.storeId;
      const newStoreId = item ? Number(item.id) : null;
      this.$set(this.form.lines[idx], 'storeId', newStoreId);
      this.$set(this.form.lines[idx], '_storeModel', item ? (item.shortName ? `${item.name} (${item.shortName})` : item.name) : '');
      if (newStoreId !== prevStoreId) {
        this.$set(this.form.lines[idx], 'costItemId', null);
        this.$set(this.form.lines[idx], '_costItemModel', '');
        this.$set(this.form.lines[idx], '_budgetAllocatedAmount', null);
        this.$set(this.form.lines[idx], '_budgetRemainingAfterPR', null);
      }
      if (newStoreId) {
        this.loadBudgetCostItemsForLine(idx);
      } else {
        this.$set(this.form.lines[idx], '_costItemSuggestions', this.costItemSource.slice(0, 20));
      }
      this.triggerLineBudgetCheck(idx);
    },
    onLineInputStore(text, idx) {
      this.$set(this.form.lines[idx], '_storeModel', text || '');
      if (!text) {
        this.$set(this.form.lines[idx], 'storeId', null);
        this.$set(this.form.lines[idx], '_costItemSuggestions', this.costItemSource.slice(0, 20));
        this.$set(this.form.lines[idx], '_budgetWarning', null);
      }
      this.$set(this.form.lines[idx], '_storeSuggestions', this.filterByName(this.storeSource, text));
    },
    loadBudgetCostItemsForLine(idx) {
      const line = this.form.lines[idx];
      if (!line) return;
      const { departmentId, companyId, month, year } = this.form;
      if (!departmentId || !month || !year) return;
      const params = { departmentId, month, year };
      if (line.storeId) params.storeId = line.storeId;
      if (companyId) params.companyId = companyId;
      ApiService.query('v2/budget-lines/approved-cost-items', { params })
        .then(({ data }) => {
          const items = data?.data || [];
          if (items.length) {
            this.$set(this.form.lines[idx], '_costItemSuggestions', items);
            this.$set(this.form.lines[idx], '_budgetWarning', null);
          } else {
            this.$set(this.form.lines[idx], '_costItemSuggestions', []);
            this.$set(
              this.form.lines[idx],
              '_budgetWarning',
              'Cửa hàng này chưa có ngân sách được duyệt cho tháng đã chọn.',
            );
          }
        })
        .catch(() => {
          this.$set(this.form.lines[idx], '_costItemSuggestions', []);
          this.$set(
            this.form.lines[idx],
            '_budgetWarning',
            'Không thể tải ngân sách. Vui lòng thử lại.',
          );
        });
    },
    onLineAmountChange(idx) {
      this.debouncedCheckBudgetLine(idx);
    },
    triggerLineBudgetCheck(idx) {
      // Load allocated immediately when costItem+store selected (SRS v1.5)
      this.checkBudgetForLine(idx);
    },

    checkBudgetForLine(idx) {
      const line = this.form.lines[idx];
      if (!line) return;
      const { departmentId, companyId, month, year } = this.form;
      if (!departmentId || !companyId || !month || !year || !line.costItemId) return;

      const params = {
        departmentId,
        costItemId: line.costItemId,
        month,
        year,
        companyId,
        requestedAmount: line.requestedAmount || 0,
        budgetUsagePeriod: line.budgetUsagePeriod || 1,
      };
      if (line.storeId) params.storeId = line.storeId;

      this.$set(this.form.lines[idx], '_budgetLoading', true);
      ApiService.query('v2/purchase-requests/budget-check', { params })
        .then(({ data }) => {
          const check = data.data;
          this.$set(this.form.lines[idx], '_budgetAllocatedAmount', check.allocatedAmount ?? null);
          this.$set(this.form.lines[idx], '_budgetRemainingAfterPR', check.remainingAfterPR ?? null);
          this.$set(this.form.lines[idx], '_budgetLineId', check.budgetLineId ?? null);
        })
        .catch(() => {
          this.$set(this.form.lines[idx], '_budgetAllocatedAmount', null);
          this.$set(this.form.lines[idx], '_budgetRemainingAfterPR', null);
        })
        .finally(() => {
          this.$set(this.form.lines[idx], '_budgetLoading', false);
        });
    },

    onYearChange(value) {
      let year = null;
      if (value == null) return;
      if (moment.isMoment(value)) year = value.year();
      else if (value instanceof Date) year = value.getFullYear();
      else if (typeof value === 'number') year = value;
      else if (typeof value === 'string') { const m = value.match(/\d{4}/); year = m ? Number(m[0]) : null; }
      if (year && year >= 2020 && year <= 2100) {
        this.form.year = year;
        this.yearModel = moment(String(year), 'YYYY');
      }
    },

    fetchCompany() {
      ApiService.get('/company').then((response) => { this.listCompany = response.data.data; });
    },
    fetchDepartments() {
      this.isLoadingFilters = true;
      return ApiService.query('departments/all')
        .then(({ data }) => {
          this.departmentSource = (data.data || []).map((d) => ({ id: Number(d.id), name: d.name }));
          this.departmentSuggestions = this.departmentSource.slice(0, this.suggestLimit);
        })
        .catch(() => { this.departmentSource = []; this.departmentSuggestions = []; })
        .finally(() => { this.isLoadingFilters = false; this.syncDepartmentModel(); });
    },
    fetchCostItems() {
      return ApiService.get('v2/cost-items/all')
        .then(({ data }) => { this.costItemSource = (data.data || []).map((i) => ({ id: Number(i.id), name: i.name })); });
    },
    fetchStores() {
      this.isLoadingFilters = true;
      return ApiService.query('stores/getStores', { params: { byEmployeeGroup: true } })
        .then(({ data }) => { this.storeSource = (data.data || []).map((s) => ({ id: Number(s.id), name: s.name, shortName: s.shortName })); })
        .catch(() => { this.storeSource = []; })
        .finally(() => { this.isLoadingFilters = false; });
    },

    onSelectedDepartment(option) {
      const item = option && option.item ? option.item : null;
      this.form.departmentId = item ? Number(item.id) : null;
      this.departmentModel = item ? item.name : '';
      this.departmentSuggestions = this.departmentSource.slice(0, this.suggestLimit);
    },
    onInputDepartment(text) {
      this.departmentModel = text || '';
      if (!text) this.form.departmentId = null;
      this.departmentSuggestions = this.filterByName(this.departmentSource, text, this.suggestLimit);
    },
    syncDepartmentModel() {
      if (!this.form.departmentId) { this.departmentModel = ''; return; }
      const found = this.departmentSource.find((d) => Number(d.id) === Number(this.form.departmentId));
      if (found) this.departmentModel = found.name;
    },

    mapApprovalSteps(details, selectedApprovers = []) {
      const selectedByStep = (selectedApprovers || []).reduce((acc, item) => { acc[item.step] = item.approverBy; return acc; }, {});
      this.approvalSteps = (details || []).sort((a, b) => a.step - b.step).map((detail) => {
        const owners = [...new Map((detail.owners || []).filter((o) => o && o.id).map((o) => [o.id, { id: Number(o.id), name: o.name || o.fullName || String(o.id), code: o.code || '' }])).values()];
        return { step: detail.step, approverBy: selectedByStep[detail.step] || null, owners };
      });
    },
    fetchApprovalConfig(selectedApprovers = []) {
      ApiService.get(`v2/approval-configs/${this.approvalType}/approvers`)
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.approvalConfigDetails = data.data.details || [];
            this.mapApprovalSteps(this.approvalConfigDetails, selectedApprovers);
          } else {
            this.approvalConfigDetails = [];
            this.approvalSteps = [];
          }
        })
        .catch(() => { this.approvalConfigDetails = []; this.approvalSteps = []; });
    },

    fetchDetail(id) {
      ApiService.get(`v2/purchase-requests/${id}`)
        .then(({ data }) => {
          const pr = data.data;
          const period = pr.period ? new Date(pr.period) : new Date();
          this.form.id = pr.id;
          this.form.companyId = pr.companyId;
          this.form.departmentId = pr.departmentId;
          this.form.month = period.getMonth() + 1;
          this.form.year = period.getFullYear();
          this.form.description = pr.description || '';
          this.yearModel = moment(String(this.form.year), 'YYYY');
          this.syncDepartmentModel();

          // Hydrate lines from PR detail
          this.form.lines = (pr.lines || []).map((line) => ({
            _key: newLineKey(),
            lineNumber: line.lineNumber,
            costItemId: line.costItemId,
            storeId: line.storeId,
            requestedAmount: line.requestedAmount,
            budgetUsagePeriod: line.budgetUsagePeriod || 1,
            description: line.description || null,
            _costItemModel: line.costItemName || '',
            _storeModel: line.storeName || '',
            _costItemSuggestions: this.costItemSource.slice(0, 20),
            _storeSuggestions: this.storeSource.slice(0, 20),
            _budgetAllocatedAmount: line.budgetAllocatedAmount ?? null,
            _budgetRemainingAfterPR: line.budgetRemainingAfterPR ?? null,
            _budgetLoading: false,
            _budgetWarning: null,
          }));
          this.$nextTick(() => {
            this.form.lines.forEach((_, i) => {
              if (this.form.lines[i].storeId) this.loadBudgetCostItemsForLine(i);
            });
          });

          this.fetchApprovalConfig(pr.approvers || []);
        })
        .catch((err) => handleError(err));
    },

    openImportModal() {
      this.showImportModal = true;
    },

    onExcelImported(importedLines) {
      const startIdx = this.form.lines.length;
      importedLines.forEach((line) => {
        this.form.lines.push({
          _key: newLineKey(),
          lineNumber: this.form.lines.length + 1,
          costItemId: line.costItemId,
          storeId: line.storeId,
          requestedAmount: line.requestedAmount,
          budgetUsagePeriod: line.budgetUsagePeriod || 1,
          description: line.description || null,
          _costItemModel: line._costItemModel || '',
          _storeModel: line._storeModel || '',
          _costItemSuggestions: this.costItemSource.slice(0, 20),
          _storeSuggestions: this.storeSource.slice(0, 20),
          _budgetAllocatedAmount: null,
          _budgetRemainingAfterPR: null,
          _budgetLoading: false,
          _budgetWarning: null,
        });
      });
      this.showImportModal = false;
      this.makeToastSuccess(`Đã import ${importedLines.length} dòng từ Excel.`);
      this.$nextTick(() => {
        for (let idx = startIdx; idx < this.form.lines.length; idx++) {
          const line = this.form.lines[idx];
          if (line.storeId) this.loadBudgetCostItemsForLine(idx);
          if (line.costItemId) this.checkBudgetForLine(idx);
        }
      });
    },

    validateForm() {
      if (!this.form.companyId) { this.makeToastFaile('Vui lòng chọn doanh nghiệp!'); return false; }
      if (!this.form.departmentId) { this.makeToastFaile('Vui lòng chọn phòng ban!'); return false; }
      if (!this.form.month || !this.form.year) { this.makeToastFaile('Vui lòng chọn tháng/năm!'); return false; }
      if (this.form.lines.length === 0) { this.makeToastFaile('Phải có ít nhất 1 dòng chi tiết!'); return false; }
      for (const line of this.form.lines) {
        if (!line.costItemId) { this.makeToastFaile(`Dòng ${line.lineNumber}: chưa chọn khoản mục phí!`); return false; }
        if (!line.requestedAmount || line.requestedAmount <= 0) { this.makeToastFaile(`Dòng ${line.lineNumber}: số tiền phải lớn hơn 0!`); return false; }
      }
      if (this.duplicateWarnings.length > 0) {
        this.makeToastFaile('Phiếu có dòng trùng (khoản mục + cửa hàng). Vui lòng kiểm tra lại!');
        return false;
      }
      if (!this.approvalSteps.length) { this.makeToastFaile('Chưa có cấu hình quy trình duyệt!'); return false; }
      const invalidStep = this.approvalSteps.find((step) => !step.approverBy);
      if (invalidStep) { this.makeToastFaile(`Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`); return false; }
      return true;
    },

    onSubmit() {
      if (!this.validateForm()) return;
      this.isSaving = true;

      const cleanLines = this.form.lines.map((line) => ({
        lineNumber: line.lineNumber,
        costItemId: line.costItemId,
        storeId: line.storeId || null,
        requestedAmount: line.requestedAmount,
        budgetUsagePeriod: line.budgetUsagePeriod || 1,
        description: line.description || null,
      }));

      const payload = {
        id: this.form.id,
        companyId: this.form.companyId,
        departmentId: this.form.departmentId,
        month: this.form.month,
        year: this.form.year,
        description: this.form.description,
        lines: cleanLines,
        approvers: this.approvalSteps
          .map((step) => ({ step: step.step, approverBy: step.approverBy }))
          .sort((a, b) => a.step - b.step),
      };

      const request = this.isEdit
        ? ApiService.put('v2/purchase-requests', payload)
        : ApiService.post('v2/purchase-requests', payload);

      request
        .then(() => {
          this.makeToastSuccess(this.isEdit ? 'Cập nhật phiếu thành công!' : 'Tạo phiếu thành công!');
          this.$router.push({ name: 'list-purchase-requests', query: { sortBy: 'createdAt' } });
        })
        .catch((err) => handleError(err))
        .finally(() => { this.isSaving = false; });
    },

    goBack() {
      this.$router.push({ name: 'list-purchase-requests' });
    },
  },
};
</script>

<style scoped>
.table th, .table td {
  vertical-align: middle;
}
</style>
